import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { NOTIFIER } from '@shared/notifier/types';
import { PlatformCreateFeedbackAttachment } from '@data/_generated-api/platform-api/models/platform-create-feedback-attachment';
import { ViewportService } from '@core/services/viewport.service';
import { SupportAttachmentDraft } from './support-attachment.models';
import { SupportImageService } from './support-image.service';

/** Bestandsnaam (zonder extensie) voor een afbeelding die van het klembord komt. */
const CLIPBOARD_FILE_STEM = 'clipboard';
const FALLBACK_PASTE_FILE_NAME = 'screenshot.jpg';

/** Alleen wat van de Clipboard API gebruikt wordt; `read` ontbreekt buiten een secure context. */
type ClipboardReader = { read?: () => Promise<ClipboardItem[]> };

/**
 * Gedeelde bijlage-inname voor het meldingsformulier en de reactie-composer:
 * klembord lezen, plak-events uitpakken, normaliseren met gebruikersmeldingen
 * en drafts omzetten naar de API-payload. De teksten blijven zo op één plek
 * (support.form.attachments.*), net als het foutgedrag.
 */
@Injectable({ providedIn: 'root' })
export class SupportAttachmentIntakeService {
  private readonly images = inject(SupportImageService);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);
  private readonly viewport = inject(ViewportService);

  /**
   * Normaliseert een blob tot een draft; bij een te grote of onleesbare
   * afbeelding krijgt de gebruiker een waarschuwing en komt er undefined terug.
   */
  async normalizeWithFeedback(blob: Blob, fileName: string): Promise<SupportAttachmentDraft | undefined> {
    try {
      return await this.images.normalize(blob, fileName);
    } catch (reason) {
      const key = reason instanceof Error && reason.message === 'tooLarge' ? 'tooLarge' : 'invalid';
      this.notifier.warning(this.transloco.translate<string>(`support.form.attachments.${key}`));
      return undefined;
    }
  }

  /**
   * Leest de eerste afbeelding van het klembord via de Clipboard API (async,
   * alleen in een secure context en met toestemming). Weigering of ontbrekende
   * API is geen fout van de gebruiker: een info-melding wijst dan op Ctrl+V.
   * Een leeg klembord meldt zich ook zelf; de aanroeper hoeft niets te tonen.
   */
  async readClipboardImage(): Promise<{ blob: Blob; fileName: string } | undefined> {
    const clipboard = navigator.clipboard as ClipboardReader | undefined;
    if (!clipboard?.read) {
      this.notifyPasteUnsupported();
      return undefined;
    }
    try {
      const image = await this.firstClipboardImage(await clipboard.read());
      if (!image) {
        this.notifier.info(this.transloco.translate<string>('support.form.attachments.pasteEmpty'));
        return undefined;
      }
      return { blob: image, fileName: `${CLIPBOARD_FILE_STEM}.${this.extensionOf(image.type)}` };
    } catch (error) {
      // NotAllowedError = toestemming geweigerd; alles daarbuiten is onverwacht en mag opvallen.
      if (!(error instanceof DOMException && error.name === 'NotAllowedError')) {
        console.warn('[support] klembord lezen mislukt', error);
      }
      this.notifyPasteUnsupported();
      return undefined;
    }
  }

  /** Op touch verwijst de melding niet naar Ctrl+V maar naar de foto-/bestandsknop. */
  private notifyPasteUnsupported(): void {
    const key = this.viewport.isTouchPrimary() ? 'pasteUnsupportedTouch' : 'pasteUnsupported';
    this.notifier.info(this.transloco.translate<string>(`support.form.attachments.${key}`));
  }

  /** Eerste afbeelding uit een paste-event (Ctrl+V), of undefined als er geen in zit. */
  imageFromPasteEvent(event: ClipboardEvent): { blob: Blob; fileName: string } | undefined {
    const files = Array.from(event.clipboardData?.files ?? []);
    const image = files.find(file => file.type.startsWith('image/'));
    if (!image) return undefined;
    return { blob: image, fileName: image.name || FALLBACK_PASTE_FILE_NAME };
  }

  /** Draft naar de API-payload; de normalisatie levert altijd jpeg. */
  async toPayload(draft: SupportAttachmentDraft): Promise<PlatformCreateFeedbackAttachment> {
    return {
      fileName: draft.fileName,
      contentType: 'image/jpeg',
      width: draft.width,
      height: draft.height,
      dataBase64: await this.toBase64(draft.blob),
    };
  }

  toPayloads(drafts: SupportAttachmentDraft[]): Promise<PlatformCreateFeedbackAttachment[]> {
    return Promise.all(drafts.map(draft => this.toPayload(draft)));
  }

  /** Eerste afbeeldingstype over alle klemborditems heen; tekst of andere types worden overgeslagen. */
  private async firstClipboardImage(items: ClipboardItem[]): Promise<Blob | undefined> {
    for (const item of items) {
      const type = item.types.find(candidate => candidate.startsWith('image/'));
      if (type) return item.getType(type);
    }
    return undefined;
  }

  /** `image/jpeg` -> `jpg`, `image/png` -> `png`; onbekend of leeg valt terug op png. */
  private extensionOf(mimeType: string): string {
    const subtype = mimeType.split('/')[1]?.split(';')[0]?.toLowerCase();
    if (!subtype) return 'png';
    return subtype === 'jpeg' ? 'jpg' : subtype;
  }

  private toBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.onerror = () => reject(reader.error ?? new Error('read'));
      reader.readAsDataURL(blob);
    });
  }
}
